/* eslint-disable react-hooks/set-state-in-effect -- Effect initializes notification toggle state from server settings on first load. */
import { BellIcon, CheckCheck, GlobeIcon, Import, Trash2 } from 'lucide-react';

import { useEffect, useRef, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { UpdateSettingsDto } from '@splitfiesta/shared';
import { Alert, Button, Card, FormSwitch } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { PreloadLink } from '@/components/common/PreloadLink';
import { AppLayout } from '@/components/layout/AppLayout';
import { AccountDeletionSection } from '@/components/settings/AccountDeletionSection';
import { GetTheAppSection } from '@/components/settings/GetTheAppSection';
import { LanguageSelector } from '@/components/settings/LanguageSelector';
import { ProSectionContainer } from '@/components/settings/ProSectionContainer';
import { ScanUsageSectionContainer } from '@/components/settings/ScanUsageSectionContainer';
import { SettingsPageSkeleton } from '@/components/skeleton';
import { SearchableCurrencySelect } from '@/components/ui/SearchableCurrencySelect';

import {
  useDeleteNotificationsMutation,
  useMarkNotificationsAsReadMutation,
  useNotificationSettingsQuery,
  useUnreadNotificationCount,
  useUpdateNotificationSettingsMutation,
} from '@/hooks/queries/useNotifications';
import {
  useUpdateSettingsMutation,
  useUserSettingsQuery,
} from '@/hooks/queries/useUser';
import { useBrowserDefaults } from '@/hooks/useBrowserDefaults';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

import { toast } from '@/utils/toast';

function SettingsPage() {
  const user = useAuthStore(state => state.user);
  const { t, ready } = useTranslation();
  const browserDefaults = useBrowserDefaults();
  const celebrationsEnabled = useUIStore(state => state.celebrationsEnabled);
  const setCelebrationsEnabled = useUIStore(
    state => state.setCelebrationsEnabled
  );
  const cloudPredictionEnabled = useUIStore(
    state => state.cloudPredictionEnabled
  );
  const setCloudPredictionEnabled = useUIStore(
    state => state.setCloudPredictionEnabled
  );

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingField, setSavingField] = useState<string | null>(null);
  const [savingNotificationField, setSavingNotificationField] = useState<
    string | null
  >(null);

  // Notification preferences state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);

  // Timeout refs for cleanup
  const successTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  // Load required namespaces
  const settingsReady = useNamespace('settings');
  const commonReady = useNamespace('common');
  const namespacesLoaded = settingsReady && commonReady;
  // Not part of `namespacesLoaded`: the import card is the only thing that
  // needs it, so the rest of the page must not wait on it.
  const importReady = useNamespace('import');

  const { isOnline } = useNetworkStatus();

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeout.current) {
        clearTimeout(successTimeout.current);
      }
    };
  }, []);

  // Use React Query for settings data
  const { data: settingsData, isLoading: settingsLoading } =
    useUserSettingsQuery(ready && namespacesLoaded && !!user);

  // Use React Query for notification settings
  const { data: notificationSettings, isLoading: notificationSettingsLoading } =
    useNotificationSettingsQuery(ready && namespacesLoaded && !!user);

  // Use mutations
  const updateSettingsMutation = useUpdateSettingsMutation();
  const updateNotificationSettingsMutation =
    useUpdateNotificationSettingsMutation();

  // Bulk notification-feed actions (distinct from the preference
  // toggles below). Mark-all-read mirrors the visible button on the
  // notifications page; clear-all lives only here so the destructive
  // action stays out of the main feed header.
  const markAllReadMutation = useMarkNotificationsAsReadMutation();
  const clearAllMutation = useDeleteNotificationsMutation();
  const unreadCount = useUnreadNotificationCount();

  // Extract settings from response
  const extractedSettings = settingsData
    ? 'currency' in settingsData
      ? settingsData
      : null
    : null;

  // Merge settings with browser defaults
  const userSettings = extractedSettings
    ? {
        currency: extractedSettings.currency || browserDefaults.currency || '',
        language: extractedSettings.language || browserDefaults.language || '',
      }
    : null;

  // Initialize notification settings when they load
  useEffect(() => {
    if (notificationSettings?.data) {
      const settings = notificationSettings.data;
      setEmailNotifications(settings.emailEnabled);
      setPushNotifications(settings.pushEnabled);
    }
  }, [notificationSettings]);

  const saveSettingField = async (
    field: 'currency' | 'language',
    value: string
  ) => {
    setSavingField(field);
    try {
      const updateData: UpdateSettingsDto = { [field]: value };
      await updateSettingsMutation.mutateAsync(updateData);

      // Show brief success message
      setSuccess(t('settings:messages.settingUpdated'));
      if (successTimeout.current) {
        clearTimeout(successTimeout.current);
      }
      successTimeout.current = setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('settings:errors.updateFailed')
      );
    } finally {
      setSavingField(null);
    }
  };

  const saveNotificationSetting = async (
    field: 'emailEnabled' | 'pushEnabled',
    value: boolean
  ) => {
    setSavingNotificationField(field);
    try {
      // Create the complete notification settings object
      // Use the passed value for the field being updated, current state for others
      const updatedSettings = {
        emailEnabled: field === 'emailEnabled' ? value : emailNotifications,
        pushEnabled: field === 'pushEnabled' ? value : pushNotifications,
      };

      await updateNotificationSettingsMutation.mutateAsync(updatedSettings);
      // Success - no feedback needed for seamless UX
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('settings:errors.updateFailed')
      );
    } finally {
      setSavingNotificationField(null);
    }
  };

  // Non-destructive and recoverable per-row, so it fires without a
  // confirm — same as the notifications-page button.
  const handleMarkAllNotificationsRead = async () => {
    try {
      await markAllReadMutation.mutateAsync({ markAll: true });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('settings:errors.updateFailed')
      );
    }
  };

  // Destructive — wipes the entire feed with no undo. Gated by a
  // confirm, matching the per-row delete contract used across web.
  const handleClearAllNotifications = async () => {
    if (!window.confirm(t('settings:notifications.clearAllConfirm'))) return;
    try {
      await clearAllMutation.mutateAsync({ deleteAll: true });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('settings:errors.updateFailed')
      );
    }
  };

  // Offline the import cannot even start: the file is parsed locally but the
  // job is created server-side.
  const renderImportButton = () => {
    const button = (
      <Button
        variant="secondary"
        leftIcon={<Import className="w-5 h-5" />}
        className="w-full sm:w-auto"
        disabled={!isOnline}
        title={!isOnline ? t('import:errors.offline') : undefined}
        data-testid="settings-import-button"
      >
        {t('import:entry.cta')}
      </Button>
    );

    // A disabled button inside a link is still focusable and still navigates,
    // so offline the wrapper goes away with the action.
    return isOnline ? (
      <PreloadLink href="/groups/import">{button}</PreloadLink>
    ) : (
      button
    );
  };

  // Show skeleton immediately while translations or data are loading
  if (
    !ready ||
    !namespacesLoaded ||
    settingsLoading ||
    notificationSettingsLoading
  ) {
    return (
      <AppLayout>
        <SettingsPageSkeleton />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('settings:title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('settings:subtitle')}
          </p>
        </div>

        {error && (
          <Alert type="error" message={error} data-testid="settings-error" />
        )}

        {success && (
          <Alert
            type="success"
            message={success}
            data-testid="settings-success"
          />
        )}

        <div className="space-y-8" data-testid="settings-form">
          {/* A Pro user's section, or a counted user's scan card, leads the page. */}
          <ProSectionContainer />

          <ScanUsageSectionContainer />

          {importReady && (
            <Card data-testid="settings-import-card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <Import className="w-5 h-5 mr-2" />
                {t('import:entry.title')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('import:entry.subtitle')}
              </p>
              {renderImportButton()}
            </Card>
          )}

          {/* App Preferences */}
          <Card data-testid="preferences-card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <GlobeIcon className="w-5 h-5 mr-2" />
              {t('settings:sections.preferences')}
            </h3>
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  {t('settings:language.title')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  {t('settings:language.description')}
                </p>
                <div className="relative">
                  <LanguageSelector
                    value={userSettings?.language || ''}
                    onChange={language =>
                      saveSettingField('language', language)
                    }
                    disabled={savingField === 'language'}
                    data-testid="language-select"
                  />
                  {savingField === 'language' && (
                    <div className="absolute right-2 top-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  {t('settings:currency.title')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  {t('settings:currency.description')}
                </p>
                <div className="relative">
                  <SearchableCurrencySelect
                    value={userSettings?.currency || ''}
                    onChange={currency =>
                      saveSettingField('currency', currency)
                    }
                    mode="group"
                    disabled={savingField === 'currency'}
                    data-testid="currency-select"
                  />
                  {savingField === 'currency' && (
                    <div className="absolute right-2 top-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                    </div>
                  )}
                </div>
              </div>

              <FormSwitch
                id="celebrationsEnabled"
                label={t('settings:celebrations.title')}
                description={t('settings:celebrations.description')}
                checked={celebrationsEnabled}
                onChange={setCelebrationsEnabled}
                data-testid="celebrations-toggle"
              />

              {/* AI (Anthropic) category suggestions — privacy-policy-
                  disclosed sharing, so the user always has this off switch. */}
              <FormSwitch
                id="cloudPredictionEnabled"
                label={t('settings:smartSuggestions.title')}
                description={t('settings:smartSuggestions.description')}
                checked={cloudPredictionEnabled}
                onChange={setCloudPredictionEnabled}
                data-testid="smart-suggestions-toggle"
              />
            </div>
          </Card>

          {/* Notification Preferences */}
          <Card data-testid="notification-settings-card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <BellIcon className="w-5 h-5 mr-2" />
              {t('settings:notifications.title')}
            </h3>

            {/* Bulk feed actions live above the preference toggles,
                separated by a divider. Mark all read mirrors the
                button on the notifications page; Clear all is the
                destructive action that lives only here. */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                {t('settings:notifications.bulkActions')}
              </h4>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkAllNotificationsRead}
                  disabled={unreadCount === 0 || markAllReadMutation.isPending}
                  leftIcon={<CheckCheck className="h-4 w-4" />}
                  data-testid="settings-mark-all-read"
                >
                  {t('settings:notifications.markAllRead')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAllNotifications}
                  disabled={clearAllMutation.isPending}
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  className="!border-error-300 !text-error-600 hover:!bg-error-50 dark:!border-error-900 dark:!text-error-500 dark:hover:!bg-error-500/10"
                  data-testid="settings-clear-all"
                >
                  {t('settings:notifications.clearAll')}
                </Button>
              </div>
            </div>

            <div className="space-y-4" data-testid="notification-toggles">
              {/* TODO: Re-enable when email delivery is fully implemented (see docs/PAYMENT_REMINDERS_PLAN.md) */}
              {/* <div className="relative">
                <FormSwitch
                  id="emailNotifications"
                  label={t('settings:notifications.email')}
                  description={t('settings:notifications.emailDesc')}
                  checked={emailNotifications}
                  onChange={async value => {
                    setEmailNotifications(value);
                    await saveNotificationSetting('emailEnabled', value);
                  }}
                  disabled={savingNotificationField === 'emailEnabled'}
                  data-testid="email-notifications-toggle"
                />
                {savingNotificationField === 'emailEnabled' && (
                  <div className="absolute right-2 top-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                  </div>
                )}
              </div> */}

              <div className="relative">
                <FormSwitch
                  id="pushNotifications"
                  label={t('settings:notifications.push')}
                  description={t('settings:notifications.pushDesc')}
                  checked={pushNotifications}
                  onChange={async value => {
                    setPushNotifications(value);
                    await saveNotificationSetting('pushEnabled', value);
                  }}
                  disabled={savingNotificationField === 'pushEnabled'}
                  data-testid="push-notifications-toggle"
                />
                {savingNotificationField === 'pushEnabled' && (
                  <div className="absolute right-2 top-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <GetTheAppSection />

          <AccountDeletionSection />
        </div>
      </div>
    </AppLayout>
  );
}

export default withAuth(SettingsPage);
